import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, HeartHandshake, HardHat, Leaf } from "lucide-react";
import { COMPANY, STATS } from "../lib/siteData";
import { LogoTile } from "../components/Logo";

const VALUES = [
    {
        icon: HardHat,
        title: "In-house craftsmanship",
        text: "Our carpenters, tilers, and site supervisors work only with us — so quality is consistent, not outsourced.",
    },
    {
        icon: HeartHandshake,
        title: "Client-first honesty",
        text: "We'd rather lose a project than talk you into one that shouldn't happen. Transparent BQs, no hidden costs.",
    },
    {
        icon: Award,
        title: "Detail-obsessed",
        text: "Skirting, cove lines, cabinet reveals — the small details are checked before hand-over, and again after.",
    },
    {
        icon: Leaf,
        title: "Built to age well",
        text: "Materials selected for the Malaysian climate. Finishes that mellow, not fade, over the years.",
    },
];

export default function AboutPage() {
    return (
        <div data-testid="page-about">
            <section className="pt-40 pb-16 max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-14 items-end">
                <div className="md:col-span-7">
                    <div className="eyebrow mb-6">About the studio</div>
                    <h1 className="font-display text-5xl md:text-7xl leading-[1] text-hhc-ink">
                        A Malaysian studio for the quietly ambitious home.
                    </h1>
                </div>
                <div className="md:col-span-5">
                    <p className="text-hhc-muted text-[17px] leading-[1.75]">
                        HEYHOMECAN is the design-build practice of{" "}
                        <span className="text-hhc-ink font-medium">
                            {COMPANY.legalName}
                        </span>{" "}
                        — a Kuala Lumpur–based team crafting interiors,
                        renovations, and cabinetry for families who care about
                        how a home ages.
                    </p>
                </div>
            </section>

            {/* Image band */}
            <section className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 md:gap-8">
                    <div className="col-span-12 md:col-span-8 rounded-2xl overflow-hidden aspect-[16/10]">
                        <img
                            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80"
                            alt="Studio project"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="col-span-12 md:col-span-4 rounded-2xl overflow-hidden aspect-[3/4] md:aspect-auto">
                        <img
                            src="https://images.unsplash.com/photo-1664638413302-d1ca29ac885b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxpbnRlcmlvciUyMGRlc2lnbiUyMG1hdGVyaWFscyUyMG1vb2Rib2FyZHxlbnwwfHx8fDE3ODM0MjEyNzF8MA&ixlib=rb-4.1.0&q=85"
                            alt="Materials"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Story */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
                    <div className="md:col-span-5">
                        <div className="eyebrow mb-6">Our story</div>
                        <h2 className="font-display text-4xl md:text-5xl text-hhc-ink leading-[1.05]">
                            Founded on a simple belief.
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7">
                        <p className="text-[17px] text-hhc-muted leading-[1.75]">
                            A renovation shouldn't feel chaotic. It should feel
                            like a slow, considered act — with drawings that
                            respect your budget and a site that respects your
                            time. HEYHOMECAN was founded to bring that quiet
                            discipline back to Malaysian home-making.
                        </p>
                        <p className="text-[17px] text-hhc-muted leading-[1.75] mt-6">
                            Today, our small studio takes on a limited number of
                            projects each year across KL and Selangor. That
                            deliberate pace is what lets us stay hands-on from
                            concept to move-in day.
                        </p>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="bg-hhc-cream/60 border-y border-hhc-line/70 py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="mb-14 max-w-2xl">
                        <div className="eyebrow mb-4">What we stand for</div>
                        <h2 className="font-display text-4xl md:text-5xl text-hhc-ink leading-[1.05]">
                            Four principles behind every project.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {VALUES.map((v) => {
                            const Icon = v.icon;
                            return (
                                <div
                                    key={v.title}
                                    className="rounded-2xl bg-white border border-hhc-line p-8 md:p-10 hover-lift"
                                    data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div className="w-12 h-12 rounded-full bg-hhc-cream text-hhc-clay grid place-items-center mb-6">
                                        <Icon size={20} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-display text-2xl text-hhc-ink">
                                        {v.title}
                                    </h3>
                                    <p className="mt-4 text-hhc-muted leading-relaxed">
                                        {v.text}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    {STATS.map((s, i) => (
                        <div key={i} data-testid={`about-stat-${i}`}>
                            <div className="font-display text-5xl md:text-6xl text-hhc-ink">
                                {s.value}
                            </div>
                            <div className="mt-2 text-[11px] tracking-[0.28em] uppercase text-hhc-muted">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Legal / studio card */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 pb-32">
                <div className="rounded-2xl bg-hhc-ink text-hhc-cream p-10 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-3 flex justify-center md:justify-start">
                        <div className="bg-hhc-sun p-4 rounded-2xl">
                            <LogoTile size={110} />
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <div className="text-[11px] tracking-[0.28em] uppercase text-hhc-sun mb-4">
                            Registered studio
                        </div>
                        <h3 className="font-display text-3xl md:text-4xl text-hhc-ivory">
                            {COMPANY.legalName}
                        </h3>
                        <p className="mt-3 text-hhc-cream/70 text-[14px]">
                            {COMPANY.ssm}
                        </p>
                        <p className="mt-4 text-hhc-cream/85 text-[15px] leading-relaxed">
                            {COMPANY.address}
                        </p>
                    </div>
                    <div className="md:col-span-3 md:text-right">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 bg-hhc-sun text-hhc-ink px-6 py-3 rounded-full font-medium hover:bg-hhc-ivory transition-colors"
                            data-testid="about-cta-contact"
                        >
                            Visit us <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
